/**
 * ensureSharedInstancesHaveStartAtIndex — client-side hook.
 *
 * Problem: startAtIndex is private (per-user). When the DO generates recurring
 * instances in the owner's context, those startAtIndex entries only exist for
 * the owner. Shared realm members receive the task object but NOT the
 * startAtIndex. Without it, the instance won't appear in date views.
 *
 * Solution: On app-start (and periodically), find recurring instances that
 * are in a shared realm and have no local startAtIndex. Create one using
 * the task's scheduled date from the occurrence key.
 */

import { db } from "@/db/schema"
import { ObjectStatus, StartAtType, StartAtSubType } from "@/db/schema"
import dayjs from "dayjs"

/**
 * Ensures all shared recurring instances have a local startAtIndex entry.
 * Only creates entries for containers the user has opted into (showInDateViews=1).
 * Safe to call multiple times (idempotent).
 */
export async function ensureSharedInstancesHaveStartAtIndex(): Promise<number> {
  // Get opted-in container IDs from sharedViewPref
  const containerPrefs = await db.sharedViewPref
    .filter((p) => p.showInDateViews === 1 && p.scope === "container")
    .toArray()
  const taskPrefs = await db.sharedViewPref
    .filter((p) => p.showInDateViews === 1 && p.scope === "task")
    .toArray()

  if (!containerPrefs.length && !taskPrefs.length) return 0

  const followedContainerIds = new Set(containerPrefs.map((p) => p.id.slice(1)))
  const followedTaskIds = new Set(taskPrefs.map((p) => p.id.slice(1)))

  // Find recurring instances in shared realms
  // Read recurring instances only, via the recurringTemplateId index, as one
  // getAll() and filter in JS. A full-table filter() visits every object in a
  // cursor and blocks every readwrite transaction on totodo until it is done.
  const instances = (
    await db.totodo.where("recurringTemplateId").above("").toArray()
  ).filter(
    (t) =>
      !!t.realmId &&
      t.realmId.startsWith("rlm~") &&
      t.status === ObjectStatus.Unfinished &&
      !t.isDeleted,
  )

  if (!instances.length) return 0

  // Filter to only instances in opted-in containers/tasks
  const optedInInstances = instances.filter((inst) => {
    if (followedTaskIds.has(inst.id!)) return true
    const containerId = inst.realmId!.replace("rlm~", "")
    return followedContainerIds.has(containerId)
  })

  if (!optedInInstances.length) return 0

  let created = 0
  const lastIdx = await db.startAtIndex.orderBy("index").last()
  let nextIndex = (lastIdx?.index ?? 0) + 1

  for (const inst of optedInInstances) {
    const entryId = `#${inst.id!}`
    const existing = await db.startAtIndex.get(entryId)
    if (existing) continue

    // Derive scheduled date from occurrenceKey (YYYYMMDD format)
    let startAtMs: number
    if (inst.occurrenceKey && /^\d{8}$/.test(inst.occurrenceKey)) {
      startAtMs = dayjs(inst.occurrenceKey, "YYYYMMDD").startOf("day").valueOf()
    } else {
      // Fallback: use createdAt or today
      startAtMs = inst.createdAt
        ? dayjs(inst.createdAt).startOf("day").valueOf()
        : dayjs().startOf("day").valueOf()
    }

    await db.startAtIndex.add({
      id: entryId,
      index: nextIndex++,
      startAt: String(startAtMs),
      type: StartAtType.Default,
      subType: StartAtSubType.Default,
    })
    created++
  }

  if (created > 0) {
    console.log(`[recurrence] Created ${created} startAtIndex entries for shared instances`)
  }
  return created
}
